
import React, { useState, useEffect } from 'react';
import { Mail, Loader2, ShieldCheck, ArrowLeft, AlertCircle, CheckCircle } from './Icons';
import { User } from '../types';
import { authService } from '../services/authService';
import { getSupabase } from '../services/supabaseClient';

interface EmailVerificationProps {
  user: User;
  onVerified: () => void;
  onBack: () => void;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({ user, onVerified, onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<string | null>(null);

  // Manually check if the user is verified (e.g., after clicking the button)
  const checkStatus = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: { user: sbUser }, error: sbError } = await getSupabase().auth.getUser();
      if (sbError) throw sbError;
      
      if (sbUser?.email_confirmed_at) {
        onVerified();
      } else {
        setError("We haven't detected the verification yet. Please make sure to click the link in the email we sent!");
        setTimeout(() => setError(null), 5000);
      }
    } catch (err: any) {
      setError("Could not check status. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResendStatus('Sending...');
    setError(null);
    try {
      await authService.resendCode(user.email);
      setResendStatus('New link sent!');
      setTimeout(() => setResendStatus(null), 5000);
    } catch (err: any) {
      setResendStatus(null);
      setError(err.message || "Failed to resend link.");
    }
  };

  // Listen for the verification event in real-time
  useEffect(() => {
    const { data: { subscription } } = getSupabase().auth.onAuthStateChange((event, session) => {
      // If the user clicks the link in another tab, this tab will pick it up
      if (session?.user?.email_confirmed_at) {
        onVerified();
      }
    });
    return () => subscription.unsubscribe();
  }, [onVerified]);

  const getEmailProviderLink = () => {
    if (user.email.includes('gmail.com')) return 'https://mail.google.com';
    if (user.email.includes('outlook.com') || user.email.includes('hotmail.com')) return 'https://outlook.live.com';
    return null;
  };

  const providerLink = getEmailProviderLink();

  return (
    <div className="w-full max-w-lg mx-auto mt-10 px-4 animate-slide-up pb-20">
      <button 
        onClick={onBack}
        className="mb-8 flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Login
      </button>

      <div className="glass-panel p-10 md:p-12 rounded-[3rem] shadow-2xl border border-white/5 relative overflow-hidden text-center">
        {/* Decorative Background Glow */}
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-gold-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 space-y-8">
            <div className="w-24 h-24 bg-gradient-to-tr from-gold-500/20 to-forge-500/20 rounded-[2rem] flex items-center justify-center text-gold-400 mx-auto shadow-glow animate-float">
                <Mail className="w-12 h-12" />
            </div>

            <div className="space-y-3">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-white leading-tight">Check Your Inbox</h2>
                <p className="text-gray-400 text-lg">
                    We've sent a magical confirmation link to <br/>
                    <span className="text-white font-bold decoration-gold-500/30 underline underline-offset-4">{user.email}</span>
                </p>
            </div>

            {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-200 text-sm animate-shake">
                    <div className="flex items-center justify-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                    </div>
                </div>
            )}

            <div className="space-y-4 pt-4">
                <button
                    onClick={checkStatus}
                    disabled={isLoading}
                    className="w-full py-5 btn-primary-gradient text-white rounded-2xl font-bold shadow-xl shadow-forge-500/20 transition-all flex justify-center items-center gap-3 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                >
                    {isLoading ? <Loader2 className="animate-spin w-6 h-6" /> : <CheckCircle className="w-6 h-6" />}
                    <span>I've Verified My Email</span>
                </button>

                <div className="flex flex-col sm:flex-row gap-3">
                    <button 
                        onClick={handleResend}
                        disabled={!!resendStatus || isLoading}
                        className="flex-1 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-white text-sm font-bold transition-all disabled:opacity-50"
                    >
                        {resendStatus || "Resend Email"}
                    </button>
                    {providerLink && (
                        <a 
                            href={providerLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-white text-sm font-bold transition-all flex items-center justify-center gap-2"
                        >
                            Open {user.email.includes('gmail') ? 'Gmail' : 'Outlook'}
                        </a>
                    )}
                </div>
            </div>

            <div className="pt-8 border-t border-white/5 flex flex-col items-center gap-4">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                    <ShieldCheck className="w-3 h-3" />
                    Secure Verification
                </div>
                <p className="text-xs text-gray-500 max-w-xs mx-auto italic">
                    Note: Verification links expire after 24 hours. Check your <strong>Spam</strong> or <strong>Promotions</strong> folder if you don't see it.
                </p>
            </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
